
import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useNavigate } from 'react-router-dom';
import { Trophy, ArrowRight, Rocket } from 'lucide-react';
import InternalLoginForm from './InternalLoginForm';
import InternalRegistrationForm from './InternalRegistrationForm';
import InternalResetForm from './InternalResetForm';
import SpecialRequestForm from './SpecialRequestForm';

const InternalAuthForm = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'login' | 'register' | 'reset' | 'special-request' | 'beta'>('register');
  const { user } = useInternalAuth();
  const navigate = useNavigate();

  // Set initial tab based on location state or default to register
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    } else if (location.state?.role || location.state?.supportRole) {
      // If coming from FAQ with role selection, default to register
      setActiveTab('register');
    }
  }, [location.state]);

  // Redirect if already logged in - route based on role
  React.useEffect(() => {
    if (user) {
      const targetPath = user.role === 'admin' ? '/internal-dashboard' : '/dashboard';
      navigate(targetPath, { replace: true });
    }
  }, [user, navigate]);

  const getTitle = () => {
    switch (activeTab) {
      case 'reset':
        return 'Account Recovery';
      case 'special-request':
        return 'Special Role Request';
      case 'login':
        return 'New School - Login';
      default:
        return 'New School - Login';
    }
  };

  const getDescription = () => {
    switch (activeTab) {
      case 'register':
        return 'Create your secure account';
      case 'login':
        return 'Sign in to your account';
      case 'reset':
        return 'Reset password or PIN using seed phrase';
      case 'special-request':
        return 'Request access to restricted roles';
      default:
        return 'Create your secure account';
    }
  };

  return (
    <Card className="w-full bg-[#0d1117] border border-gray-800 shadow-2xl rounded-none md:rounded-lg">
      <CardHeader className="border-b border-gray-800 px-4 md:px-6">
        <CardTitle className="text-xl md:text-2xl text-sports-green text-center">
          {getTitle()}
        </CardTitle>
        <CardDescription className="text-center text-gray-400 text-sm md:text-base">
          {getDescription()}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4 md:pt-6 px-4 md:px-6">
        {activeTab === 'reset' ? (
          <InternalResetForm onBack={() => setActiveTab('login')} />
        ) : activeTab === 'special-request' ? (
          <SpecialRequestForm onBack={() => setActiveTab('register')} />
        ) : activeTab === 'beta' ? (
          <div className="space-y-4">
            <div className="text-center space-y-2">
              <div className="mx-auto w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Rocket className="h-6 w-6 text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">BETA Login</h3>
              <p className="text-sm text-gray-400">
                Access your Founding Agent application status and complete your registration.
              </p>
            </div>
            <button
              onClick={() => navigate('/agent/application-status')}
              className="w-full py-3 px-4 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              Go to BETA Login
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => setActiveTab('login')}
              className="w-full py-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
              ← Back to regular login
            </button>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={(v) => {
            setActiveTab(v as 'login' | 'register');
            // Scroll to top when switching tabs
            window.scrollTo(0, 0);
          }} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-4 md:mb-6 bg-gray-900">
              <TabsTrigger value="login" className="text-white data-[state=active]:bg-sports-green data-[state=active]:text-black text-sm md:text-base">Login</TabsTrigger>
              <TabsTrigger value="register" className="text-white data-[state=active]:bg-sports-green data-[state=active]:text-black text-sm md:text-base">Register</TabsTrigger>
              <TabsTrigger value="beta" className="text-white data-[state=active]:bg-orange-500 data-[state=active]:text-white text-sm md:text-base">BETA</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <InternalLoginForm onForgotPassword={() => setActiveTab('reset')} />
            </TabsContent>
            
            <TabsContent value="register">
              <InternalRegistrationForm onSpecialRequest={() => setActiveTab('special-request')} />
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
      {activeTab !== 'reset' && activeTab !== 'special-request' && activeTab !== 'beta' && (
        <div className="justify-center border-t border-gray-800 pt-4 px-4 md:px-6">
          <p className="text-xs md:text-sm text-center text-gray-400 pb-4">
            {activeTab === 'register' ? (
              <>
                Already have an account?{' '}
                <button 
                  type="button" 
                  onClick={() => {
                    setActiveTab('login');
                    window.scrollTo(0, 0);
                  }}
                  className="text-sports-green hover:underline"
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                Don't have an account?{' '}
                <button 
                  type="button"
                  onClick={() => {
                    setActiveTab('register');
                    window.scrollTo(0, 0);
                  }}
                  className="text-sports-green hover:underline"
                >
                  Sign up
                </button>
              </>
            )}
          </p>
        </div>
      )}
      
      {/* Agent Beta CTA Section */}
      <div className="mx-4 md:mx-6 mb-4 md:mb-6 p-4 rounded-lg bg-gradient-to-r from-orange-500/10 to-orange-500/5 border border-orange-500/30">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-orange-500/20">
            <Trophy className="h-5 w-5 text-orange-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-white">
                Agent, Scout, or NIL Professional?
              </h3>
              <span className="px-2 py-0.5 text-xs font-bold bg-orange-500/20 text-orange-400 rounded">
                BETA
              </span>
            </div>
            <p className="text-xs text-gray-400 mb-3">
              Join our exclusive Founding Agent Beta program with special pricing and early access to athlete discovery tools.
            </p>
            <Link 
              to="/agent-beta" 
              className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-400 hover:text-orange-300 transition-colors"
            >
              Apply for Agent Beta Access
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default InternalAuthForm;
